import type { CustomTranslateKey } from '@/basket/types/i18n';
import type { SurveyFormValues } from '@/components/base/customer-survey/customer-survey';
import ErrorText from '@/components/base/form/error-text/error-text';
import RadioButton from '@/components/base/form/input/radio-button';
import TextArea from '@/components/base/form/input/text-area';
import { useTranslations } from 'next-intl';
import type { ChangeEvent } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

interface Props {
  options: string[];
  type: 'radio' | 'textarea';
  optional?: boolean;
  name: keyof SurveyFormValues;
  testId?: string;
}

export const SurveyQuestion = ({
  options,
  type,
  optional,
  name,
  testId,
}: Props) => {
  const { control, setValue } = useFormContext<SurveyFormValues>();
  const t = useTranslations('common');

  const onChangeValue = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;
    setValue(name as keyof SurveyFormValues, value, {
      shouldValidate: true,
    });
  };

  return (
    <div className="flex flex-col" data-testid={testId}>
      <p className="flex gap-xs pb-xxs text-default text-input-label-bold">
        {t(`survey.${name}.question`)}
        {optional && (
          <span className="text-input-label text-muted italic">(optional)</span>
        )}
      </p>
      {type === 'textarea' && (
        <Controller
          render={({ field: { value, onBlur, onChange } }) => (
            <TextArea
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              resize="vertical"
            />
          )}
          name={name}
        />
      )}
      {type === 'radio' && (
        <div className="flex flex-col">
          <Controller
            control={control}
            name={name}
            render={({
              field: { value: formValue },
              fieldState: { error },
            }) => (
              <>
                {options.map((option, index) => (
                  <div className="py-xxs" key={option}>
                    <RadioButton
                      onChange={onChangeValue}
                      value={option}
                      name={name}
                      error={error}
                      testId={`survey-answer-${index + 1}`}
                      checked={option === formValue}
                      id={`${name}-${option}`}
                      key={option}
                      label={t(
                        `survey.${name}.options.${option}` as CustomTranslateKey,
                      )}
                    />
                  </div>
                ))}
                {error?.message && (
                  <ErrorText>
                    {t(error.message as CustomTranslateKey)}
                  </ErrorText>
                )}
              </>
            )}
          />
        </div>
      )}
    </div>
  );
};
